import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy
} from "@angular/core";
import { Validators, NonNullableFormBuilder } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subscription } from "rxjs";
import { take, skip, distinctUntilChanged } from "rxjs/operators";
import {
  GroupAddress,
  GroupAddressService
} from "@onsip/common/services/api/resources/groupAddress/group-address.service";
import {
  UserAddress,
  UserAddressService
} from "@onsip/common/services/api/resources/userAddress/user-address.service";
import {
  AdvVoicemailbox,
  VoicemailService
} from "@onsip/common/services/api/resources/voicemail/voicemail.service";
import {
  AddressSummary,
  AddressSummaryService
} from "../../../../../../../common/services/api/resources/addressSummary/address-summary.service";
import {
  UserAlias,
  UserAliasService
} from "../../../../../../../common/services/api/resources/userAlias/user-alias.service";
import { SnackbarService } from "../../../../../shared/components/snackbar/snackbar.service";
import { AllAddressType } from "@onsip/common/services/api/resources/addressSummary/address-summary";
import {
  AdvVoicemailboxWithExt,
  EmailOptions,
  VoicemailBasicInfo
} from "../../../admin-voicemail-types";
import { ModalMaterialComponent } from "../../../../../shared/components/modal/modal-material.component";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import {
  ApiPromiseState,
  ApiPromiseSubject
} from "@onsip/common/services/api/api-promise-state.service";
import { NAME_VALIDATOR_REGEX } from "@onsip/common/libraries/validators/name-validators";

@Component({
  selector: "onsip-admin-voicemail-basic-edit",
  templateUrl: "./admin-voicemail-basic-edit.component.html",
  styleUrls: [
    "./admin-voicemail-basic-edit.component.scss",
    "../../admin-voicemail-shelf-container.component.scss"
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdminVoicemailBasicEditComponent implements OnInit, OnDestroy {
  @Input() voicemail!: AdvVoicemailboxWithExt;
  @Output() viewPanel = new EventEmitter<void>();
  formGroup = this.formbuilder.group({
    mailboxOwner: this.formbuilder.control(""),
    mailboxName: this.formbuilder.control("", [
      // same validations for any name inputs - we can possibly make a common name valiator?
      Validators.maxLength(60),
      Validators.pattern(NAME_VALIDATOR_REGEX)
    ]),
    mailboxNumber: this.formbuilder.control("", [
      Validators.min(1),
      Validators.pattern("^[0-9]*$")
    ]),
    mailboxPin: this.formbuilder.control("", [Validators.pattern("^([0-9]{0,6}|10000000)$")]),
    notificationEmail: this.formbuilder.control(""),
    attachAsWav: this.formbuilder.control(false),
    deleteAfterSending: this.formbuilder.control(false),
    mwi: this.formbuilder.control("")
  });
  E2eLocators = E2eLocators;
  loadingStateComplete$ = new BehaviorSubject(true);

  private listOfAddressSummary: Array<AddressSummary> = [];
  private listOfAlias: Array<UserAlias> = [];
  private unsubscriber = new Subscription();

  constructor(
    private translate: TranslateService,
    private formbuilder: NonNullableFormBuilder,
    private voicemailService: VoicemailService,
    private snackbar: SnackbarService,
    private userAddress: UserAddressService,
    private groupAddress: GroupAddressService,
    private addressSummaryService: AddressSummaryService,
    private userAlias: UserAliasService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.formGroup.setValue({
      mailboxOwner: this.voicemail.voicemailboxOwner,
      mailboxName: this.voicemail.name,
      mailboxNumber: this.getOnlyNumbers(this.voicemail.mailbox),
      mailboxPin: this.voicemail.password,
      notificationEmail: this.voicemail.email,
      attachAsWav: this.voicemail.emailOptions.attach !== EmailOptions.NO,
      deleteAfterSending: this.voicemail.emailOptions.attach === EmailOptions.YES_THEN_DELETE,
      mwi: this.voicemail.mwi
    });

    // disable the checkboxes if there is no notification email
    if (!this.voicemail.email) {
      this.formGroup.controls.attachAsWav.disable();
      this.formGroup.controls.deleteAfterSending.disable();
    }
    // set initial set of delete after sending checkbox correctly based of attachAsWav value
    if (!this.formGroup.controls.attachAsWav.value) {
      this.formGroup.controls.deleteAfterSending.disable();
    }

    this.unsubscriber.add(
      this.formGroup.controls.notificationEmail.valueChanges.subscribe(value => {
        if (value && this.formGroup.controls.notificationEmail.valid) {
          this.formGroup.controls.attachAsWav.enable();
        } else {
          this.formGroup.controls.attachAsWav.setValue(false);
          this.formGroup.controls.attachAsWav.disable();
          this.formGroup.controls.deleteAfterSending.setValue(false);
          this.formGroup.controls.deleteAfterSending.disable();
        }
      })
    );
    this.unsubscriber.add(
      this.formGroup.controls.attachAsWav.valueChanges.subscribe(value => {
        if (value) {
          this.formGroup.controls.deleteAfterSending.enable();
        } else {
          this.formGroup.controls.deleteAfterSending.setValue(false);
          this.formGroup.controls.deleteAfterSending.disable();
        }
      })
    );

    this.unsubscriber.add(
      // skip the initial value changes, 1st with the empty string, and then 2nd with the voicemail owner value
      // we don't want to modify the initial mailbox name
      this.formGroup.controls.mailboxOwner.valueChanges.pipe(skip(2)).subscribe(owner => {
        const address = this.listOfAddressSummary.find(address => address.aor === owner);
        const extension = this.listOfAlias.find(
          alias => alias.addressId === address?.addressId
        )?.aliasUsername;
        if (address) this.formGroup.controls.mailboxName.setValue(address.name);
        if (extension) {
          this.formGroup.controls.mailboxNumber.setValue(this.getOnlyNumbers(extension));
        }
      })
    );

    this.unsubscriber.add(
      this.addressSummaryService.orgState.subscribe(addressSummary => {
        this.listOfAddressSummary = addressSummary;
      })
    );

    this.unsubscriber.add(
      this.userAlias.orgState.subscribe(userAlias => {
        this.listOfAlias = userAlias;
      })
    );

    this.filterPasswordField();
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  togglePanel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) this.viewPanel.next();
        })
    );
  }

  onSubmit(): void {
    const requestsP: Array<ApiPromiseState<UserAddress | GroupAddress | AdvVoicemailbox>> = [];
    if (this.formGroup.valid) {
      this.loadingStateComplete$.next(false);
      const voicemailBasicInfo: VoicemailBasicInfo = this.formGroup.getRawValue();
      const emailOptions = this.getEmailOptions(voicemailBasicInfo);
      if (this.hasUpdatedVoicemailParams(voicemailBasicInfo)) {
        const params = {
          AdvVoicemailboxId: this.voicemail.advVoicemailboxId,
          Name: voicemailBasicInfo.mailboxName,
          Mailbox: voicemailBasicInfo.mailboxNumber,
          Password: voicemailBasicInfo.mailboxPin,
          Email: voicemailBasicInfo.notificationEmail,
          "EmailOptions[attach]": emailOptions,
          Mwi: voicemailBasicInfo.mwi,
          // things not modified but api requires these inputs
          Pager: this.voicemail.pager,
          Timezone: this.voicemail.timezone,
          SayCallerId: this.voicemail.sayCallerId,
          SayDateTime: this.voicemail.sayDateTime,
          SayDuration: this.voicemail.sayDuration,
          TransferAddressUsername: this.voicemail.transferAddress?.username || ""
        };
        const req = this.voicemailService.advVoicemailboxEdit(params);
        req && requestsP.push(req);
      }
      this.handleChangeVoicemailOwner(voicemailBasicInfo.mailboxOwner, requestsP);

      Promise.all(requestsP)
        .finally(() => {
          this.clearErrors();
        })
        .then(res => {
          if (res.every(item => item?.status !== "error")) {
            this.handleSuccess();
          } else {
            const error = res.find(item => item?.status === "error" && item.data.message)?.data
              .message as string;
            this.handleError(error);
          }
        })
        .catch(() => {
          this.handleError();
        });
    }
  }

  getMailboxNameError(): string {
    const mailboxName = this.formGroup.controls.mailboxName;
    return mailboxName.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.MUST_BE_LESS_THAN_60_CHARACTERS")
      : mailboxName.hasError("pattern")
      ? this.translate.instant(
          "ONSIP_I18N.MUST_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_SPACES_AND_NO_PLUS_COMMA_COLON"
        )
      : mailboxName.hasError("required")
      ? this.translate.instant("ONSIP_I18N.NAME_IS_REQUIRED")
      : "";
  }

  getMailboxNumberError(): string {
    const mailboxNumber = this.formGroup.controls.mailboxNumber;
    return mailboxNumber.hasError("min")
      ? "Must be greater than 1"
      : mailboxNumber.hasError("pattern")
      ? "Mailbox can only contain numbers"
      : mailboxNumber.hasError("required")
      ? "Mailbox number is required"
      : "";
  }

  getMailboxPinError(): string {
    const mailboxPin = this.formGroup.controls.mailboxPin;
    return mailboxPin.hasError("pattern")
      ? this.translate.instant("ONSIP_I18N.PASSWORD_CANNOT_BE_MORE_THAN_SIX_DIGITS_LONG")
      : mailboxPin.hasError("required")
      ? this.translate.instant("ONSIP_I18N.PASSWORD_IS_REQUIRED")
      : "";
  }

  private handleError(error?: string) {
    error =
      error ||
      (this.translate.instant("ONSIP_I18N.THERE_WAS_AN_ERROR_SAVING_YOUR_INFORMATION") as string);
    this.snackbar.openSnackBar(error, "error");
    this.loadingStateComplete$.next(true);
  }

  private handleSuccess() {
    this.snackbar.openSnackBar(
      this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
      "success"
    );
    this.loadingStateComplete$.next(true);
    this.viewPanel.emit();
  }

  private clearErrors() {
    this.voicemailService.clearErrors();
    this.userAddress.clearErrors();
    this.groupAddress.clearErrors();
  }

  private getEmailOptions(formGroupValue: VoicemailBasicInfo): EmailOptions {
    const notifyEmail = formGroupValue.notificationEmail;
    const attachAsWav = formGroupValue.attachAsWav;
    const deleteAfterSending = formGroupValue.deleteAfterSending;

    // if there is no notification email, return no
    if (!notifyEmail) return EmailOptions.NO;
    if (attachAsWav && !deleteAfterSending) return EmailOptions.YES;
    if (attachAsWav && deleteAfterSending) return EmailOptions.YES_THEN_DELETE;

    // we can send an email with no wav file option here
    return EmailOptions.NO;
  }

  private hasUpdatedVoicemailParams(formGroupValue: VoicemailBasicInfo): boolean {
    return (
      formGroupValue.mailboxName !== this.voicemail.name ||
      formGroupValue.mailboxNumber !== this.voicemail.mailbox ||
      formGroupValue.mailboxPin !== this.voicemail.password ||
      formGroupValue.mwi !== this.voicemail.mwi ||
      formGroupValue.notificationEmail !== this.voicemail.email ||
      this.getEmailOptions(formGroupValue) !== this.voicemail.emailOptions.attach
    );
  }

  private handleChangeVoicemailOwner(
    newOwner: string,
    requestsP: Array<ApiPromiseState<UserAddress | GroupAddress | AdvVoicemailbox>>
  ) {
    const previousOwner = this.voicemail.voicemailboxOwner;
    if (previousOwner === newOwner) return;

    const previousAddressType = this.getOwnerAddressType(previousOwner);
    const newAddressType = this.getOwnerAddressType(newOwner);

    if (newAddressType === "user") {
      requestsP.push(this.editUserAddress(newOwner, this.voicemail.voicemailboxAddress?.aor || ""));
    } else if (newAddressType === "group") {
      requestsP.push(
        this.editGroupAddress(newOwner, this.voicemail.voicemailboxAddress?.aor || "")
      );
    }
    // need to set previous owner and set its default address to none
    if (previousAddressType === "user") {
      requestsP.push(this.editUserAddress(previousOwner, ""));
    } else if (previousAddressType === "group") {
      requestsP.push(this.editGroupAddress(previousOwner, ""));
    }
  }

  private editUserAddress(
    voicemailOwner: string,
    DefaultAddress: string
  ): ApiPromiseState<UserAddress> {
    return new Promise((resolve, reject) => {
      this.unsubscriber.add(
        this.userAddress.orgState.pipe(take(1)).subscribe(userAddresses => {
          const newAddress = userAddresses.find(userAddress => userAddress.aor === voicemailOwner);
          if (newAddress) {
            const req = this.userAddress.userAddressEditDefaultAddress({
              Address: newAddress.aor,
              // I don't know why we hard set the timeout to an invalid value so reseting the max number to 120
              // don't want to mess with the types file for now as we use userAddress in a lot of places
              Timeout: newAddress.timeout > 120 ? 120 : newAddress.timeout,
              DefaultAddress
            });
            if (req) {
              req.then(res => resolve(res)).catch(e => reject(e));
            } else {
              reject();
            }
          } else {
            resolve({ status: "success" } as ApiPromiseSubject<UserAddress>);
          }
        })
      );
    });
  }

  private editGroupAddress(
    voicemailOwner: string,
    DefaultAddress: string
  ): ApiPromiseState<GroupAddress> {
    return new Promise((resolve, reject) => {
      this.unsubscriber.add(
        this.groupAddress.orgState.pipe(take(1)).subscribe(groups => {
          const newAddress = groups.find(
            group => group.username + "@" + group.address.domain === voicemailOwner
          );
          if (newAddress) {
            const req = this.groupAddress.groupAddressEdit({
              Address: newAddress.address.username + "@" + newAddress.address.domain,
              DefaultAddress,
              Ordering: newAddress.ordering,
              Timeout: newAddress.timeout,
              SelectedAddresses: newAddress.groupMemberAddresses.map(
                add => add.address.username + "@" + add.address.domain
              )
            });
            if (req) {
              req.then(res => resolve(res)).catch(e => reject(e));
            } else {
              reject();
            }
          } else {
            resolve({ status: "success" } as ApiPromiseSubject<GroupAddress>);
          }
        })
      );
    });
  }

  private getOwnerAddressType(owner: string): AllAddressType | "" {
    if (owner === "") return "";
    return this.listOfAddressSummary.find(address => address.aor === owner)?.addressType || "";
  }

  private getOnlyNumbers(str: string) {
    return str?.replace(/\D/g, "");
  }

  private filterPasswordField() {
    this.unsubscriber.add(
      this.formGroup.controls.mailboxPin.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe(value => {
          const filterOnlyNumbers = value.replace(/\D/g, "");
          this.formGroup.controls.mailboxPin.patchValue(filterOnlyNumbers);
        })
    );
  }
}
